import {useViewer} from '@github-ui/item-picker-assignee-relay/useViewer'
import {
  inspectFilterBranchesInSearchQuery,
  replaceFilterBranchInSearchQuery,
  tokenizeQuery,
  type FilterBranchQueryInspection,
} from '@github-ui/filter-query'
import {ActionList, ActionMenu} from '@primer/react'
import type {HTMLAttributes, RefObject} from 'react'

import {createQuickFilterAnchor, type QuickFilterProps} from './quick-filter-shared'
import {getRepoPullsFilterState} from './quick-filter-query'

type ReviewFilter = {
  key: 'review' | 'reviewed-by' | 'user-review-requested' | 'review-requested'
  value: string
  label: string
  negated?: boolean
}

const REVIEW_FILTERS: readonly ReviewFilter[] = [
  {key: 'review', value: 'none', label: 'No reviews'},
  {key: 'review', value: 'required', label: 'Review required'},
  {key: 'review', value: 'approved', label: 'Approved review'},
  {key: 'review', value: 'changes_requested', label: 'Changes requested'},
] as const

const VIEWER_REVIEW_FILTERS: readonly ReviewFilter[] = [
  {key: 'reviewed-by', value: '@me', label: 'Reviewed by you'},
  {key: 'reviewed-by', value: '@me', label: 'Not reviewed by you', negated: true},
  {key: 'user-review-requested', value: '@me', label: 'Awaiting review from you'},
  {key: 'review-requested', value: '@me', label: 'Awaiting review from you or your team'},
] as const

const REVIEW_FILTER_KEYS = new Set<ReviewFilter['key']>([
  'review',
  'reviewed-by',
  'user-review-requested',
  'review-requested',
])
const REVIEW_FILTER_TYPES = [...REVIEW_FILTER_KEYS]
const REVIEW_FILTER_PATTERN =
  /(^|[\s(])-?(?:review|reviewed-by|user-review-requested|review-requested):("([^"]*)"|[^,\s)]+)(\s*,\s*("(?:\\"|.)*?"|[^,\s]+))*/gim

const reviewAnchor = createQuickFilterAnchor('Reviews')

function ReviewAnchor({ref, ...props}: HTMLAttributes<HTMLElement> & {ref?: RefObject<HTMLButtonElement | null>}) {
  return reviewAnchor(props, ref)
}

function inspectReviewQuery(query: string) {
  return inspectFilterBranchesInSearchQuery(query, REVIEW_FILTER_TYPES)
}

function getSelectedStructuralReviewFilter(
  inspection: FilterBranchQueryInspection,
  reviewFilters: readonly ReviewFilter[],
) {
  if (
    !inspection.isSafe ||
    (inspection.hasBooleanStructure &&
      (inspection.branches.length !== 1 || inspection.branches[0]?.values.length !== 1))
  ) {
    return
  }

  for (const branch of inspection.branches.toReversed()) {
    for (const value of branch.values.toReversed()) {
      const selectedFilter = reviewFilters.find(
        filter =>
          branch.key === filter.key &&
          branch.isNegated === Boolean(filter.negated) &&
          value.toLowerCase() === filter.value,
      )
      if (selectedFilter) return selectedFilter
    }
  }
}

function getSelectedLegacyReviewFilter(query: string, reviewFilters: readonly ReviewFilter[]) {
  for (const segment of tokenizeQuery(query).toReversed()) {
    if (segment.type !== 'filter') continue
    for (const value of segment.values.toReversed()) {
      const selectedFilter = reviewFilters.find(
        filter =>
          segment.key.toLowerCase() === filter.key &&
          segment.isNegated === Boolean(filter.negated) &&
          value.toLowerCase() === filter.value,
      )
      if (selectedFilter) return selectedFilter
    }
  }
}

export function getReviewFilterState(query: string, structuralUpdatesEnabled: boolean): string | undefined {
  return getRepoPullsFilterState(query, REVIEW_FILTER_TYPES, structuralUpdatesEnabled)
}

function replaceFlatReviewFilters(query: string, filter: Pick<ReviewFilter, 'key' | 'value' | 'negated'> | undefined) {
  const querySegments = tokenizeQuery(query)
  const queryWithoutReviewFilters =
    querySegments.length === 1 && querySegments[0]?.type === 'text' && querySegments[0].value === query
      ? query.replace(REVIEW_FILTER_PATTERN, '$1').trim()
      : querySegments
          .filter(
            segment =>
              segment.type !== 'filter' || !REVIEW_FILTER_KEYS.has(segment.key.toLowerCase() as ReviewFilter['key']),
          )
          .map(segment => (segment.type === 'filter' ? segment.raw : segment.value))
          .join(' ')
          .trim()
  const reviewFilter = filter ? `${filter.negated ? '-' : ''}${filter.key}:${filter.value}` : ''

  return [queryWithoutReviewFilters, reviewFilter].filter(Boolean).join(' ')
}

export function replaceReviewFilter(
  query: string,
  filter: Pick<ReviewFilter, 'key' | 'value' | 'negated'> | undefined,
  structuralUpdatesEnabled: boolean,
) {
  if (!structuralUpdatesEnabled) {
    return filter ? replaceFlatReviewFilters(query, filter) : query
  }

  const inspection = inspectReviewQuery(query)
  if (!inspection.isSafe || inspection.hasBooleanStructure) {
    return replaceFilterBranchInSearchQuery(query, filter?.value, 'review', {
      matchingTypes: REVIEW_FILTER_TYPES,
      replacementType: filter?.key ?? 'review',
      isNegated: filter?.negated,
      allowNegatedMatch: true,
    })
  }

  return replaceFlatReviewFilters(query, filter)
}

export function ReviewQuickFilter({query, onQueryChange, structuralUpdatesEnabled}: QuickFilterProps) {
  const viewer = useViewer()
  const reviewFilters = viewer ? [...REVIEW_FILTERS, ...VIEWER_REVIEW_FILTERS] : REVIEW_FILTERS
  const selectedReviewFilter = structuralUpdatesEnabled
    ? getSelectedStructuralReviewFilter(inspectReviewQuery(query), reviewFilters)
    : getSelectedLegacyReviewFilter(query, reviewFilters)

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <ReviewAnchor />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {reviewFilters.map(filter => (
            <ActionList.Item
              key={`${filter.negated ? '-' : ''}${filter.key}:${filter.value}`}
              selected={filter === selectedReviewFilter}
              onSelect={() =>
                onQueryChange(
                  replaceReviewFilter(
                    query,
                    structuralUpdatesEnabled && filter === selectedReviewFilter ? undefined : filter,
                    structuralUpdatesEnabled,
                  ),
                  'reviews',
                )
              }
            >
              {filter.label}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
